import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { compressImage } from "@/lib/imageCompress";
import { toast } from "sonner";
import { X, Upload, Trash2, Loader2, ImageOff, Maximize2 } from "lucide-react";

const SIDES = [["front", "Ön Yüz"], ["back", "Arka Yüz"]];

export default function ChequeImagesModal({ cheque, onClose, onChanged }) {
  const [ch, setCh] = useState(cheque);
  const update = (c) => { setCh(c); onChanged?.(c); };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="cheque-images-modal"
        className="bg-white border border-black w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <h2 className="font-display text-xl font-bold">Çek Görüntüleri</h2>
            <p className="text-xs text-neutral-500 font-mono-num mt-0.5">No: {ch.cheque_number} · {ch.bank_name}</p>
          </div>
          <button data-testid="cheque-images-close" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 grid sm:grid-cols-2 gap-4">
          {SIDES.map(([side, label]) => (
            <ImageSlot key={side} cheque={ch} side={side} label={label} onUpdated={update} />
          ))}
        </div>
        <div className="px-5 pb-5 text-[11px] text-neutral-500 border-l-2 border-black ml-5 pl-3">
          Görüntüler yüklenmeden önce otomatik küçültülür (yaklaşık 250 KB). JPG, PNG veya WEBP seçebilirsiniz.
        </div>
      </div>
    </div>
  );
}

function ImageSlot({ cheque, side, label, onUpdated }) {
  const meta = cheque.images?.[side];
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!meta) { setUrl(null); return; }
    let obj; let alive = true;
    api.get(`/cheques/${cheque.id}/images/${side}`, { responseType: "blob", params: { v: meta.uploaded_at } })
      .then(({ data }) => { obj = URL.createObjectURL(data); if (alive) setUrl(obj); })
      .catch(() => alive && setUrl(null));
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [cheque.id, side, meta]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error("Sadece JPG, PNG veya WEBP seçin"); return; }
    setBusy(true);
    try {
      const small = await compressImage(file);
      const fd = new FormData(); fd.append("file", small);
      const { data } = await api.post(`/cheques/${cheque.id}/images/${side}`, fd);
      onUpdated(data);
      toast.success(`${label} yüklendi (${Math.round(small.size / 1024)} KB)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yüklenemedi");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`${label} görüntüsü silinsin mi?`)) return;
    const { data } = await api.delete(`/cheques/${cheque.id}/images/${side}`);
    onUpdated(data); toast.success("Silindi");
  };

  return (
    <div className="border border-black/15" data-testid={`cheque-image-slot-${side}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-black/10 bg-neutral-50">
        <span className="label-caps text-[10px]">{label}</span>
        <div className="flex gap-1">
          {url && (
            <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`cheque-image-open-${side}`}
              className="p-1 hover:bg-neutral-200" title="Tam boyut aç"><Maximize2 size={13} /></a>
          )}
          {meta && (
            <button data-testid={`cheque-image-delete-${side}`} onClick={remove} className="p-1 hover:bg-rose-50" title="Sil">
              <Trash2 size={13} className="text-rose-600" />
            </button>
          )}
        </div>
      </div>
      <div className="aspect-[16/9] bg-neutral-100 flex items-center justify-center overflow-hidden">
        {busy ? <Loader2 className="animate-spin text-neutral-500" size={22} />
          : url ? <img src={url} alt={label} data-testid={`cheque-image-preview-${side}`} className="w-full h-full object-contain" />
          : meta ? <Loader2 className="animate-spin text-neutral-400" size={18} />
          : <div className="text-neutral-400 text-xs flex flex-col items-center gap-1"><ImageOff size={20} />Görüntü yok</div>}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
        data-testid={`cheque-image-input-${side}`} onChange={onPick} />
      <button data-testid={`cheque-image-upload-${side}`} disabled={busy} onClick={() => inputRef.current?.click()}
        className="w-full py-2 text-xs border-t border-black/10 hover:bg-neutral-50 flex items-center justify-center gap-1.5 disabled:opacity-50">
        <Upload size={12} /> {meta ? "Değiştir" : "Görüntü Seç"}
      </button>
    </div>
  );
}
